import { isEmpty, isInstance, isNullish, notEmpty, recordValues } from "@de-xima/xima-common-js-lang";

/**
 * Attempts to locate the widget for an element, given the ID (not widgetVar) of the main DOM element of the widget (not the widget.jq.id)
 * @typeParam T Type of the widget to find.
 * @param targetId ID of the DOM element of the widget.
 * @param widgetClass Expected type of the widget. No check is performed if not given.
 * @returns The widget for the element, if found.
 */
export function findWidgetById<T extends new (...args: never[]) => unknown>(
  targetId: string,
  widgetClass: T,
): InstanceType<T> | undefined {
  if (isEmpty(targetId) || widgetClass === undefined) {
    return undefined;
  }
  for (const widgetVar of Object.keys(PrimeFaces.widgets)) {
    if (!(widgetVar in PrimeFaces.widgets)) {
      continue;
    }
    const widget = PrimeFaces.widgets[widgetVar];
    if (isNullish(widget)) {
      // Widget existed previously, but was removed via AJAX
      continue;
    }
    const jq = widget.jq;
    const id = jq?.attr("id") ?? "";
    if (id === targetId) {
      return findWidgetByVar(widgetVar, widgetClass);
    }
  }
  return undefined;
}

/**
 * Attempts to locate the widget for an element, given its widget variable.
 * @template T Type of the widget to find.
 * @param widgetVar Name of the widget variable of the widget to find.
 * @param widgetClass Expected type of the widget. No check is performed if not given.
 * @returns The widget with the given widget variable.
 */
export function findWidgetByVar<T extends new (...args: never[]) => unknown>(
  widgetVar: string,
  widgetClass: T,
): InstanceType<T> | undefined {
  const widget = PrimeFaces.widgets[widgetVar];
  if (isNullish(widget) || widgetClass === undefined) {
    return undefined;
  }
  if (!isInstance(widget, widgetClass)) {
    console.error(`Widget for var '${widgetVar}' not of the correct class ${String(widgetClass)}`);
    return undefined;
  }
  return widget as InstanceType<T> | undefined;
}

/**
 * Tries to find the widget for the given element. The following attempts are made:
 * - Search the list of all known widgets for a widget whose DOM element contains the
 * given element.
 * - Search for a widget with the same ID as the ID of the UI widget. The UI widget
 * is located by searching for the nearest parent with the class <code>ui-widget</code>
 * - Check whether containing UI widget has a data-widget-var attribute and search
 * for a widget with that widget variable.
 * @template T Type of the widget to find.
 * @param el Element for which to find the widget instance.
 * @param widgetClass Expected type of the widget. No check is performed if not given.
 * @returns The widget for the element, if found.
 */
export function findWidgetByElement<T extends new (...args: never[]) => unknown>(
  el: HTMLElement,
  widgetClass: T,
): InstanceType<T> | undefined {
  const element = el;
  if (element === undefined) {
    return undefined;
  }

  // Try the DOM element
  for (const widget of recordValues(PrimeFaces.widgets)) {
    if (widget instanceof widgetClass) {
      const mainElement = widget.getJQ().get(0);
      if (mainElement !== undefined && (mainElement === element || mainElement.contains(element))) {
        return widget as InstanceType<T>;
      }
    }
  }

  // Find the main DOM element representing the widget container
  const selector = ".ui-widget,.ui-layout-container,.ui-inplace,.ui-calendar";
  const container = element.matches(selector) ? element : element.closest(selector);

  // Try to find the widget variable
  const widgetVar = container instanceof HTMLElement ? container.dataset.widgetVar : "";
  if (notEmpty(widgetVar)) {
    return findWidgetByVar(widgetVar, widgetClass);
  }

  // Try the ID
  return findWidgetById(container?.id ?? "", widgetClass);
}
